import React from 'react';
import { Prospect } from '../../shared/types';
import { Card } from '../../components/ui/Card';
import { Search, Compass, Target, CheckCircle2, TrendingUp, DollarSign, ShieldAlert, Award } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';

interface DiagnosisSectionProps {
  prospect: Prospect;
}

export const DiagnosisSection: React.FC<DiagnosisSectionProps> = ({ prospect }) => {
  const isGastronomia = prospect.availableDemos?.includes('gastronomia');
  const isImobiliaria = prospect.availableDemos?.includes('imobiliaria');

  return (
    <section className="py-20 border-b border-slate-100 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200 mb-4">
            <Compass className="w-4 h-4 text-[var(--ym-prospect-primary)]" />
            <span className="text-xs font-bold uppercase tracking-wider">Estudo de Presença & Inteligência Regional</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mt-2">
            Diagnóstico de Mercado — {prospect.trade_name}
          </h2>
          <p className="text-slate-600 mt-4 text-base md:text-lg font-light leading-relaxed">
            Análise consultiva estratégica focada na região de <strong>{prospect.city} / {prospect.state}</strong> para acelerar a margem de lucro e o posicionamento da sua marca.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Card 1: Diagnóstico Regional de Comissões & Oportunidades */}
          <Card className="border-slate-200 bg-white p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-amber-50 text-amber-600 border border-amber-200">
                <ShieldAlert className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                   {isGastronomia ? 'Desperdício de Comissões em Marketplace' : 'Oportunidades de Captura Digital'}
                </h3>
                <p className="text-xs text-slate-500">Análise de canais e concorrência local</p>
              </div>
            </div>

            <ul className="space-y-6 text-sm text-slate-600">
              <li className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="amber">CENÁRIO DA REGIÃO</Badge>
                </div>
                <p className="text-slate-600 leading-relaxed font-light">
                  {isGastronomia 
                    ? `Plataformas de terceiros (ex: iFood) cobram de 12% a 27% de comissão por cada pedido em ${prospect.city}. Para um negócio de ${prospect.segment.toLowerCase()} faturar R$ 40 mil/mês, isso representa R$ 8.000,00 deixados na mesa todos os meses.`
                    : `Sua marca conta com excelente reconhecimento local em ${prospect.city}, porém parte expressiva da demanda regional que busca ativamente por ${prospect.segment.toLowerCase()} no Google é capturada por intermediários.`}
                </p>
              </li>

              <li className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="indigo">HIPÓTESE DE LUCRO</Badge>
                </div>
                <p className="text-slate-600 leading-relaxed font-light">
                  {isGastronomia
                    ? `Migrar apenas 35% das suas vendas para o seu próprio Cardápio Digital Web/App reduz custos de comissões e financia todas as suas ações de tráfego pago no Instagram e Google.`
                    : `Um ecossistema próprio com canal direto de atendimento e aplicativo web gera autonomia comercial e dobra o valor do cliente ao longo do ano (Lifetime Value).`}
                </p>
              </li>
            </ul>
          </Card>

          {/* Card 2: Estratégia de Posicionamento & Dominação Google */}
          <Card className="border-slate-200 bg-white p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Estratégia de Dominação Regional</h3>
                <p className="text-xs text-emerald-600">Google Rank #1 & Atendimento Direto</p>
              </div>
            </div>

            <ul className="space-y-6 text-sm text-slate-600">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-900 block mb-1">Rank #1 no Google Local:</strong>
                  <p className="text-slate-500 font-light leading-relaxed">
                    Otimização de SEO para colocar a <strong>{prospect.trade_name}</strong> no topo das buscas no Google quando qualquer cliente pesquisar por {prospect.segment.toLowerCase()} em {prospect.city}.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-900 block mb-1">Clube de Fidelidade & Cashback Próprio:</strong>
                  <p className="text-slate-500 font-light leading-relaxed">
                    Ferramentas de retenção para transformar compradores casuais em clientes fiéis que pedem diretamente pelo seu canal semanalmente.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-900 block mb-1">Gestão Profissional de Tráfego Pago:</strong>
                  <p className="text-slate-500 font-light leading-relaxed">
                    Anúncios altamente segmentados por geolocalização no Instagram e Meta Ads para atrair os melhores consumidores da região.
                  </p>
                </div>
              </li>
            </ul>
          </Card>

        </div>
      </div>
    </section>
  );
};
